'use client';

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { GraduationCap, Users, Award, BadgeCheck } from "lucide-react";

interface StatItem {
  id: string;
  value: number;
  label: string;
  suffix?: string;
  icon?: any;
}

const StatisticsSection = () => {
  const [stats, setStats] = useState<StatItem[]>([]);
  const [counts, setCounts] = useState<number[]>([]);

  useEffect(() => {
    // 🔥 Updated NEW SCHOOL stats
    const initialStats = [
      { id: "1", value: 2025, label: "Year Established", suffix: "", icon: GraduationCap },
      { id: "2", value: 15, label: "Dedicated Teachers", suffix: "", icon: Users },
      { id: "3", value: 12, label: "Modern Classrooms", suffix: "", icon: Award },
      { id: "4", value: 1, label: "Strong Vision", suffix: "", icon: BadgeCheck }
    ];

    setStats(initialStats);
    setCounts(initialStats.map(() => 0));
  }, []);

  // COUNT-UP ANIMATION
  useEffect(() => {
    stats.forEach((stat, index) => {
      let start = 0;
      const end = stat.value;
      const duration = 1500;
      const increment = end / (duration / 16);

      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          start = end;
          clearInterval(timer);
        }
        setCounts(prev => {
          const updated = [...prev];
          updated[index] = Math.floor(start);
          return updated;
        });
      }, 16);
    });
  }, [stats]);

  return (
    <section data-component-id="src/app/StatisticsSection.tsx:56:4" data-component-path="src/app/StatisticsSection.tsx" data-component-line="56" data-component-file="StatisticsSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22w-full%20mt-%5B28px%5D%20sm%3Amt-%5B56px%5D%20relative%20overflow-hidden%22%7D"
      className="w-full mt-[28px] sm:mt-[56px] relative overflow-hidden"
      style={{
        backgroundImage: "url(/images/stat.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div data-component-id="src/app/StatisticsSection.tsx:64:6" data-component-path="src/app/StatisticsSection.tsx" data-component-line="64" data-component-file="StatisticsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%22%7D" className="absolute inset-0"></div>

      <div data-component-id="src/app/StatisticsSection.tsx:66:6" data-component-path="src/app/StatisticsSection.tsx" data-component-line="66" data-component-file="StatisticsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20w-full%20max-w-%5B1440px%5D%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%20py-%5B50px%5D%20sm%3Apy-%5B80px%5D%22%7D" className="relative w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-[50px] sm:py-[80px]">

        <motion.div data-component-id="src/app/StatisticsSection.tsx:68:8" data-component-path="src/app/StatisticsSection.tsx" data-component-line="68" data-component-file="StatisticsSection.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22flex%20flex-col%20sm%3Aflex-row%20gap-%5B30px%5D%20sm%3Agap-%5B60px%5D%20md%3Agap-%5B120px%5D%20justify-center%20items-center%20w-full%20max-w-%5B700px%5D%20mx-auto%22%7D"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="flex flex-col sm:flex-row gap-[30px] sm:gap-[60px] md:gap-[120px] justify-center items-center w-full max-w-[700px] mx-auto"
        >
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;

            return (
              <motion.div data-component-id="src/app/StatisticsSection.tsx:79:14" data-component-path="src/app/StatisticsSection.tsx" data-component-line="79" data-component-file="StatisticsSection.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22flex%20flex-col%20justify-start%20items-center%20w-%5B82px%5D%22%7D"
                key={stat.id}
                initial={{ opacity: 0, scale: 0.7 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="flex flex-col justify-start items-center w-[82px]"
              >

                {/* ICON BOX */}
                <motion.div data-component-id="src/app/StatisticsSection.tsx:89:16" data-component-path="src/app/StatisticsSection.tsx" data-component-line="89" data-component-file="StatisticsSection.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22w-%5B82px%5D%20h-%5B82px%5D%20flex%20items-center%20justify-center%22%7D"
                  whileHover={{ scale: 1.1 }}
                  className="w-[82px] h-[82px] flex items-center justify-center"
                >
                  <IconComponent data-component-id="src/app/StatisticsSection.tsx:93:18" data-component-path="src/app/StatisticsSection.tsx" data-component-line="93" data-component-file="StatisticsSection.tsx" data-component-name="IconComponent" data-component-content="%7B%22elementName%22%3A%22IconComponent%22%2C%22className%22%3A%22w-10%20h-10%20text-white%20drop-shadow-md%22%7D" className="w-10 h-10 text-white drop-shadow-md" />
                </motion.div>

                {/* ANIMATED NUMBER */}
                <span data-component-id="src/app/StatisticsSection.tsx:97:16" data-component-path="src/app/StatisticsSection.tsx" data-component-line="97" data-component-file="StatisticsSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B20px%5D%20sm%3Atext-%5B36px%5D%20font-bold%20text-white%20mt-%5B14px%5D%20sm%3Amt-%5B28px%5D%20drop-shadow-lg%22%7D" className="text-[20px] sm:text-[36px] font-bold text-white mt-[14px] sm:mt-[28px] drop-shadow-lg">
                  {counts[index]}
                  {stat.suffix}
                </span>

                {/* LABEL */}
                <span data-component-id="src/app/StatisticsSection.tsx:103:16" data-component-path="src/app/StatisticsSection.tsx" data-component-line="103" data-component-file="StatisticsSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B10px%5D%20sm%3Atext-%5B16px%5D%20text-gray-200%20font-medium%20mt-%5B8px%5D%20sm%3Amt-%5B16px%5D%20text-center%20leading-%5B14px%5D%20sm%3Aleading-%5B20px%5D%22%7D" className="text-[10px] sm:text-[16px] text-gray-200 font-medium mt-[8px] sm:mt-[16px] text-center leading-[14px] sm:leading-[20px]">
                  {stat.label}
                </span>

              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default StatisticsSection;
